import { parseOneAddress } from "email-addresses";
import { z } from "zod";
import { ChannelType } from "./channel-type";

export { ChannelType } from "./channel-type";

const MAX_PREHEADER_LENGTH = 140;

/** Parse a bare email or RFC 5322 "Name <email>" into { email, name? }. */
export function parseReplyTo(
  value: string,
): { email: string; name?: string } | null {
  const parsed = parseOneAddress(value);
  if (!parsed || parsed.type !== "mailbox") return null;
  return { email: parsed.address, name: parsed.name ?? undefined };
}

const replyToField = z
  .union([
    z
      .string()
      .refine(
        (v) => parseReplyTo(v) !== null,
        "Enter a valid reply-to (e.g. Name <email@example.com>)",
      ),
    z.literal(""),
  ])
  .optional();

export enum MessageQuietHours {
  Respect = "respect",
  Ignore = "ignore",
}

export enum MessageFrequencyCapping {
  Respect = "respect",
  Ignore = "ignore",
}

export enum MessageRepeatSends {
  NotAllowed = "notAllowed",
  Cooldown = "cooldown",
}

export enum MessageAttributionMethod {
  All = "all",
  Matching = "matching",
}

// Subscription policy for opt-in/opt-out management
export enum SubscriptionPolicy {
  OptIn = "opt_in",
  OptOut = "opt_out",
  DoubleOptIn = "double_opt_in",
}

export enum CampaignScheduleType {
  Recurring = "recurring",
  OneTime = "one_time",
  JourneyTriggered = "journey_triggered",
}

export enum CampaignEndMode {
  Indefinite = "indefinite",
  SpecificDate = "specific_date",
  MaxRecurrences = "max_recurrences",
}

export type TimezoneMode = "fixed" | "recipient_local";

export enum DeviceTargeting {
  AllDevices = "all_devices",
  MostRecentOnly = "most_recent_only",
}

export enum AppVersionTargeting {
  AllVersions = "all_versions",
  SpecificVersions = "specific_versions",
}

/**
 * Default `messages.targeting_config` for a push message created without
 * explicit targeting — the dispatcher's `parseDeviceTargeting` requires it.
 */
export const DEFAULT_PUSH_TARGETING_CONFIG = {
  deviceTargeting: DeviceTargeting.AllDevices,
  appVersionTargeting: AppVersionTargeting.AllVersions,
} as const;

export enum AudienceTargeting {
  All = "all",
  NewEntrantsOnly = "new_entrants_only",
}

export enum IosBadgeCountMode {
  NoChange = "no_change",
  Set = "set",
  Increment = "increment",
}

export type IosBadgeConfig =
  | { mode: IosBadgeCountMode.NoChange }
  | { mode: IosBadgeCountMode.Set; value: number }
  | { mode: IosBadgeCountMode.Increment; value: number };

/**
 * Parse a raw `push_config.ios_badge_config` JSONB value into a typed
 * `IosBadgeConfig`. Returns `null` for missing or malformed input — callers
 * treat `null` as "leave badge unchanged" when constructing the APNs payload.
 */
export function parseIosBadgeConfig(raw: unknown): IosBadgeConfig | null {
  if (raw == null || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (obj.mode === IosBadgeCountMode.NoChange) {
    return { mode: IosBadgeCountMode.NoChange };
  }
  if (
    (obj.mode === IosBadgeCountMode.Set ||
      obj.mode === IosBadgeCountMode.Increment) &&
    typeof obj.value === "number"
  ) {
    return { mode: obj.mode, value: obj.value };
  }
  return null;
}

export enum IosInterruptionLevel {
  Active = "active",
  TimeSensitive = "time-sensitive",
  Passive = "passive",
  Critical = "critical",
}

export enum AndroidDeliveryPriority {
  Normal = "normal",
  High = "high",
}

export type PushTargetingConfig = z.infer<typeof pushTargetingSchema>;
export type MessageTargetingConfig = z.infer<typeof messageTargetingSchema>;

const audienceTargetingField = z.enum([
  AudienceTargeting.All,
  AudienceTargeting.NewEntrantsOnly,
]);

// TODO: Drop `.passthrough()` here and make these schemas strict (it currently
// tolerates extra fields on existing data).
const pushTargetingBaseSchema = z
  .object({
    deviceTargeting: z.enum([
      DeviceTargeting.AllDevices,
      DeviceTargeting.MostRecentOnly,
    ]),
    appVersionTargeting: z.enum([
      AppVersionTargeting.AllVersions,
      AppVersionTargeting.SpecificVersions,
    ]),
    appVersions: z.array(z.string()).default([]),
  })
  .passthrough();

export const pushTargetingSchema = pushTargetingBaseSchema.superRefine(
  (data, ctx) => {
    if (
      data.appVersionTargeting === AppVersionTargeting.SpecificVersions &&
      data.appVersions.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: "array",
        inclusive: true,
        path: ["appVersions"],
        message: "Select at least one version",
      });
    }
  },
);

/** Shape persisted to messages.targeting_config. Audience targeting applies to
 *  all channels; push fields are only populated for push messages. */
export const messageTargetingSchema = pushTargetingBaseSchema.partial().extend({
  audienceTargeting: audienceTargetingField,
});

/** Gmail's sender guidelines: no reply/forward prefix on bulk subject lines. */
const NO_REPLY_PREFIX = /^(?!\s*(?:re|fwd?)\s*:)/i;

/** Subject rules that hold regardless of context. Callers that relax `min(1)`
 *  use this rather than respelling the field, so rules added here reach them. */
export const subjectSchema = z
  .string()
  .regex(NO_REPLY_PREFIX, "Subject cannot start with RE:, FW: or FWD:");

export const campaignContentMetadataSchema = z
  .object({
    subject: subjectSchema.min(1, "Subject is required"),
    preheader: z
      .string()
      .max(
        MAX_PREHEADER_LENGTH,
        `Pre-header cannot exceed ${MAX_PREHEADER_LENGTH} characters`,
      )
      .optional(),
  })
  .passthrough();

const subscriptionTopicField = z
  .string()
  .min(1, "Subscription topic is required");

// Topic required unless the message is transactional. Apply after relaxing subscriptionTopic to optional.
export const requireTopicUnlessTransactional = (
  v: { transactional?: boolean; subscriptionTopic?: string },
  ctx: z.RefinementCtx,
): void => {
  if (!v.transactional && !v.subscriptionTopic) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["subscriptionTopic"],
      message: "Subscription topic is required",
    });
  }
};

// Transactional messages have no topic to derive a sender from, so the sender
// is required. Email/SMS only — push transactional has no sender field.
export const requireSenderIdentityIfTransactional = (
  v: { transactional?: boolean; senderIdentityId?: string },
  ctx: z.RefinementCtx,
): void => {
  if (v.transactional && !v.senderIdentityId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["senderIdentityId"],
      message: "Sender identity is required",
    });
  }
};

export const campaignBasicsSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().optional(),
    subscriptionTopic: subscriptionTopicField,
    replyTo: replyToField,
    contentMetadata: campaignContentMetadataSchema,
  })
  .passthrough();

/** SMS basics have no subject/preheader — only name + subscription topic. */
export const smsCampaignBasicsSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().optional(),
    subscriptionTopic: subscriptionTopicField,
  })
  .passthrough();

/** Push basics — same as SMS (no subject/preheader). */
export const pushCampaignBasicsSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().optional(),
    subscriptionTopic: subscriptionTopicField,
  })
  .passthrough();

export const createCampaignSchema = z
  .object({
    name: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, "Campaign name is required")),
    scheduleType: z.enum(
      [
        CampaignScheduleType.Recurring,
        CampaignScheduleType.OneTime,
        CampaignScheduleType.JourneyTriggered,
      ],
      { required_error: "Schedule type is required" },
    ),
    channel: z.enum([ChannelType.Email, ChannelType.Sms, ChannelType.Push], {
      message: "Channel is required",
    }),
  })
  .passthrough();

// Cap (days) on time-windowed gates. Must stay below user_sends retention
// (100d) so a max window never reads a dropped partition.
export const MAX_GATE_WINDOW_DAYS = 90;
export const MAX_COOLDOWN_HOURS = MAX_GATE_WINDOW_DAYS * 24;

const coerceOptionalNumber = z.preprocess(
  (val) =>
    val === "" || val === null || val === undefined ? null : Number(val),
  z
    .number()
    .positive("Must be positive")
    .int("Must be a whole number")
    .nullable(),
);

// Admits 0 so the sendLimits refinement can phrase it with sibling context ("…per hour").
const coerceOptionalWholeNumber = z.preprocess(
  (val) =>
    val === "" || val === null || val === undefined ? null : Number(val),
  z
    .number()
    .int("Must be a whole number")
    .min(0, "Must be a positive number")
    .nullable(),
);

// Total-send cap ("Cap total messages"); lifetime never resets, per_run resets each run.
export const VOLUME_CAP_RESETS = ["lifetime", "per_run"] as const;
export type VolumeCapReset = (typeof VOLUME_CAP_RESETS)[number];

export const volumeCapSchema = z.object({
  enabled: z.boolean(),
  maxSends: coerceOptionalWholeNumber.optional(),
  reset: z.enum(VOLUME_CAP_RESETS),
});

// Resolved cap (only when enabled with a valid maxSends). Explicit type because
// the backend tsconfig isn't strict, so z.infer collapses to all-optional.
export type VolumeCap = { maxSends: number; reset: VolumeCapReset };

// Validation boundary for frontend-written send_gates: returns null (no cap)
// when disabled, missing, or malformed.
export function parseVolumeCap(sendGates: unknown): VolumeCap | null {
  if (sendGates == null || typeof sendGates !== "object") return null;
  const raw = (sendGates as Record<string, unknown>).volumeCap;
  const parsed = volumeCapSchema.safeParse(raw);
  if (!parsed.success || !parsed.data.enabled) return null;
  const maxSends = parsed.data.maxSends;
  if (maxSends == null || maxSends <= 0) return null;
  return { maxSends, reset: parsed.data.reset };
}

export const SEND_RATE_PERIODS = ["minute", "hour", "day"] as const;
export type SendRatePeriod = (typeof SEND_RATE_PERIODS)[number];

export const SECONDS_PER_SEND_RATE_PERIOD: Record<SendRatePeriod, number> = {
  minute: 60,
  hour: 3600,
  day: 86_400,
};

// "duration" mode derives its rate from the audience and drops the unsent tail once the window plus a proportional grace passes (parseSendDurationExpiry, getDurationGraceHours).
export const SEND_RATE_MODES = ["rate", "duration"] as const;
export type SendRateMode = (typeof SEND_RATE_MODES)[number];

export const DURATION_UNITS = ["hours", "days"] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

export const HOURS_PER_DURATION_UNIT: Record<DurationUnit, number> = {
  hours: 1,
  days: 24,
};

// Ceiling when the live cepOutboxExpiryHours window isn't resolvable; mirrors DEFAULT_OUTBOX_EXPIRY_HOURS.
export const MAX_DURATION_HOURS = 7 * 24;

// `limit`/`period` belong to rate mode, `durationValue`/`durationUnit` to duration
// mode; each mode's fields are optional here and required by its parser/refinement.
export const sendRateSchema = z.object({
  enabled: z.boolean(),
  mode: z.enum(SEND_RATE_MODES).default("rate"),
  limit: coerceOptionalWholeNumber.optional(),
  period: z.enum(SEND_RATE_PERIODS).optional(),
  durationValue: coerceOptionalWholeNumber.optional(),
  durationUnit: z.enum(DURATION_UNITS).default("days"),
});

// Resolved rate for admit pacing (only when enabled with a valid limit).
export type SendRateLimit = { maxSends: number; periodSeconds: number };

export function parseSendRateLimit(sendGates: unknown): SendRateLimit | null {
  if (sendGates == null || typeof sendGates !== "object") return null;
  const raw = (sendGates as Record<string, unknown>).sendRate;
  const parsed = sendRateSchema.safeParse(raw);
  if (!parsed.success || !parsed.data.enabled) return null;
  // Duration mode's rate is derived live (resolveDurationPacingRate), not stored.
  if (parsed.data.mode === "duration") return null;
  const { limit, period } = parsed.data;
  if (limit == null || limit <= 0 || period == null) return null;
  return {
    maxSends: limit,
    periodSeconds: SECONDS_PER_SEND_RATE_PERIOD[period],
  };
}

// Identity string for the active rate config; a change resets the accrual anchor.
export function rateConfigFingerprint(rate: SendRateLimit): string {
  return `${rate.maxSends}:${rate.periodSeconds}`;
}

// Estimated seconds to send `audienceCount` at the given rate. The admit bucket
// carries its remainder, so throughput tracks this rate and audience ÷ rate is
// faithful — off only by the fresh-ramp and wave cadence, hence "Estimated".
export function estimateSendDurationSeconds(
  audienceCount: number,
  rate: SendRateLimit,
): number {
  if (rate.maxSends <= 0 || audienceCount <= 0) return 0;
  return (audienceCount / rate.maxSends) * rate.periodSeconds;
}

export function formatSendDurationEstimate(seconds: number): string {
  const units: [number, string][] = [
    [86_400, "day"],
    [3600, "hour"],
    [60, "minute"],
  ];
  for (const [secs, name] of units) {
    if (seconds >= secs) {
      const n = Math.round(seconds / secs);
      return `${n} ${name}${n === 1 ? "" : "s"}`;
    }
  }
  return "under a minute";
}

// Resolved drop deadline (hours), set only in Duration mode; else the operational default applies.
export type MessageExpiration = { hours: number };

/**
 * `maxDurationHours` must be the caller's resolved `cepOutboxExpiryHours`: a window can't
 * outlive the outbox, and the static default would let a since-lowered flag be exceeded.
 */
export function parseSendDurationExpiry(
  sendGates: unknown,
  maxDurationHours: number = MAX_DURATION_HOURS,
): MessageExpiration | null {
  if (sendGates == null || typeof sendGates !== "object") return null;
  const raw = (sendGates as Record<string, unknown>).sendRate;
  const parsed = sendRateSchema.safeParse(raw);
  if (!parsed.success || !parsed.data.enabled) return null;
  if (parsed.data.mode !== "duration") return null;
  const value = parsed.data.durationValue;
  if (value == null || value <= 0) return null;
  const hours = value * HOURS_PER_DURATION_UNIT[parsed.data.durationUnit];
  return { hours: Math.min(hours, maxDurationHours) };
}

export function formatSendDurationWindow(hours: number): string {
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

type SendRateFields = z.infer<typeof sendRateSchema>;
type VolumeCapFields = z.infer<typeof volumeCapSchema>;

const issue = (ctx: z.RefinementCtx, path: string[], message: string) =>
  ctx.addIssue({ code: z.ZodIssueCode.custom, message, path });

function validateVolumeCap(
  cap: VolumeCapFields | null | undefined,
  ctx: z.RefinementCtx,
) {
  if (!cap?.enabled) return;
  if (cap.maxSends == null || cap.maxSends <= 0) {
    issue(
      ctx,
      ["volumeCap", "maxSends"],
      "Cap must be greater than 0 total messages",
    );
  }
}

function validateSendRate(
  rate: SendRateFields | null | undefined,
  maxDurationHours: number,
  ctx: z.RefinementCtx,
) {
  if (!rate?.enabled) return;
  if (rate.mode === "duration") {
    const { durationValue, durationUnit } = rate;
    if (durationValue == null || durationValue <= 0) {
      issue(
        ctx,
        ["sendRate", "durationValue"],
        `Duration must be greater than 0 ${durationUnit}`,
      );
    } else if (
      durationValue * HOURS_PER_DURATION_UNIT[durationUnit] >
      maxDurationHours
    ) {
      issue(
        ctx,
        ["sendRate", "durationValue"],
        `Maximum duration is ${formatSendDurationWindow(maxDurationHours)}`,
      );
    }
    return;
  }
  if (rate.limit == null || rate.limit <= 0) {
    issue(
      ctx,
      ["sendRate", "limit"],
      rate.period
        ? `Rate must be greater than 0 messages per ${rate.period}`
        : "Rate must be greater than 0 messages",
    );
  }
  if (!rate.period) {
    issue(ctx, ["sendRate", "period"], "Select a rate period");
  }
}

// maxDurationHours lets the cap track the live cepOutboxExpiryHours flag.
export const buildCampaignSendLimitsSchema = ({
  maxDurationHours = MAX_DURATION_HOURS,
}: { maxDurationHours?: number } = {}) =>
  z
    .object({
      volumeCap: volumeCapSchema.optional().nullable(),
      sendRate: sendRateSchema.optional().nullable(),
      repeatSend: z.object({
        mode: z.string().min(1),
        cooldownHours: z
          .number()
          .positive("Must be positive")
          .int("Must be a whole number")
          .max(
            MAX_COOLDOWN_HOURS,
            `Cooldown cannot exceed ${MAX_GATE_WINDOW_DAYS} days`,
          )
          .optional(),
      }),
    })
    .passthrough()
    .superRefine((data, ctx) => {
      validateVolumeCap(data.volumeCap, ctx);
      validateSendRate(data.sendRate, maxDurationHours, ctx);
      if (
        data.repeatSend.mode === "cooldown" &&
        !data.repeatSend.cooldownHours
      ) {
        issue(ctx, ["repeatSend", "cooldownHours"], "Enter a cooldown period");
      }
    });

export const campaignSendLimitsSchema = buildCampaignSendLimitsSchema();

/** Per-channel user frequency cap; periodDays bounded by MAX_GATE_WINDOW_DAYS. */
export const frequencyCapConfigSchema = z.object({
  enabled: z.boolean(),
  maxSends: z
    .number()
    .positive("Must be positive")
    .int("Must be a whole number"),
  periodDays: z
    .number()
    .positive("Must be positive")
    .int("Must be a whole number")
    .max(
      MAX_GATE_WINDOW_DAYS,
      `Period cannot exceed ${MAX_GATE_WINDOW_DAYS} days`,
    ),
});

export const messageConfigurationSchema = z
  .object({
    contentMetadata: campaignContentMetadataSchema,
    senderIdentityId: z.string().min(1, "Sender identity is required"),
    subscriptionTopic: subscriptionTopicField,
    replyTo: replyToField,
    sendLimits: campaignSendLimitsSchema,
    transactional: z.boolean().optional(),
    audienceTargeting: audienceTargetingField,
  })
  .passthrough();

/** SMS messages have no subject/preheader — only sender + subscription + send limits. */
export const smsMessageConfigurationSchema = z
  .object({
    senderIdentityId: z.string().min(1, "Sender identity is required"),
    subscriptionTopic: subscriptionTopicField,
    sendLimits: campaignSendLimitsSchema,
    transactional: z.boolean().optional(),
    audienceTargeting: audienceTargetingField,
  })
  .passthrough();

/** Push messages — subscription topic + send limits + targeting (no sender identity). */
export const pushMessageConfigurationSchema = z
  .object({
    subscriptionTopic: subscriptionTopicField,
    sendLimits: campaignSendLimitsSchema,
    transactional: z.boolean().optional(),
    audienceTargeting: audienceTargetingField,
    pushTargeting: pushTargetingSchema,
  })
  .passthrough();

const campaignSchedulingFields = {
  campaignEndMode: z.enum([
    CampaignEndMode.Indefinite,
    CampaignEndMode.SpecificDate,
    CampaignEndMode.MaxRecurrences,
  ]),
  maxRecurrences: coerceOptionalNumber.optional(),
  timezoneMode: z.enum(["fixed", "recipient_local"]).optional(),
  scheduleConfig: z
    .object({
      // Wall-clock date "YYYY-MM-DD". Legacy rows may hold an ISO timestamp;
      // scheduler and validator detect and parse that case.
      startDate: z.string().optional(),
      // Wall-clock time "HH:mm" (24h), resolved against `timezone` (fixed) or
      // UTC+14 (recipient_local) at evaluation time.
      startTime: z.string().optional(),
      endDate: z.string().optional(),
      timezone: z.string().optional(),
    })
    .passthrough()
    .nullable()
    .optional(),
};

const campaignSchedulingObject = z.object(campaignSchedulingFields);

/**
 * Resolve a "YYYY-MM-DD" + "HH:mm" wall-clock in IANA `tz` to UTC ms; `null` if
 * malformed. Uses an Intl offset probe, resolved at eval time (not save time)
 * so DST/tz-rule changes are picked up. Probes twice to handle wall-clocks
 * straddling a DST boundary; nonexistent spring-forward times shift past the gap.
 */
export function wallClockToUtcMs(
  date: string,
  time: string,
  tz: string,
): number | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dateMatch || !timeMatch) return null;
  const [, yyyy, mm, dd] = dateMatch;
  const [, hh, mi] = timeMatch;
  const year = Number(yyyy);
  const month = Number(mm);
  const day = Number(dd);
  const hour = Number(hh);
  const minute = Number(mi);
  if (month < 1 || month > 12 || hour > 23 || minute > 59 || day < 1) {
    return null;
  }
  const dateOnlyMs = Date.UTC(year, month - 1, day);
  const normalizedDate = new Date(dateOnlyMs);
  if (
    normalizedDate.getUTCFullYear() !== year ||
    normalizedDate.getUTCMonth() !== month - 1 ||
    normalizedDate.getUTCDate() !== day
  ) {
    return null;
  }
  const naiveUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  // Re-pack `instant` as displayed in `tz` into UTC ms; the delta from
  // `instant` is the tz offset.
  const probe = (instant: number): number => {
    const parts = fmt.formatToParts(new Date(instant));
    const get = (t: string): number =>
      Number(parts.find((p) => p.type === t)?.value);
    return Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour") % 24, // Intl emits "24" for midnight; normalize.
      get("minute"),
      get("second"),
    );
  };
  const candidate = naiveUtcMs + (naiveUtcMs - probe(naiveUtcMs));
  const tzMsAtCandidate = probe(candidate);
  // Round-trip match → first-pass offset was right. Otherwise the candidate
  // crossed a DST transition; recompute with the offset at the candidate.
  if (tzMsAtCandidate === naiveUtcMs) return candidate;
  const adjusted = naiveUtcMs + (candidate - tzMsAtCandidate);
  const tzMsAtAdjusted = probe(adjusted);
  if (tzMsAtAdjusted === naiveUtcMs) return adjusted;

  // Spring-forward gap: the wall-clock doesn't exist. The two offsets bracket
  // the gap; use the later instant so the schedule shifts past it.
  if (tzMsAtCandidate > naiveUtcMs && tzMsAtAdjusted < naiveUtcMs) {
    return candidate;
  }
  return adjusted;
}

/**
 * Resolve a campaign schedule's start instant to UTC ms, from a wall-clock +
 * `tz` (recipient_local passes `Etc/GMT-14`) or a legacy ISO `startDate` (has a
 * `T`). `null` when no usable anchor exists — callers treat that as "skip".
 */
export function resolveScheduleStartMs(
  schedule: { startDate?: string; startTime?: string },
  tz: string,
): number | null {
  const { startDate, startTime } = schedule;
  if (!startDate) return null;
  if (startDate.includes("T")) {
    const ms = new Date(startDate).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (!startTime) return null;
  return wallClockToUtcMs(startDate, startTime, tz);
}

/**
 * Resolve a schedule's end instant (campaign expiry) to UTC ms; `null` when
 * malformed. Wall-clock `endDate` means end-of-day in `tz` (next day's midnight)
 * so the final day fires fully; legacy ISO `endDate` is parsed directly.
 */
export function resolveScheduleEndMs(
  endDate: string | undefined,
  tz: string,
): number | null {
  if (!endDate) return null;
  if (endDate.includes("T")) {
    const ms = new Date(endDate).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endDate);
  if (!match) return null;
  const [, yyyy, mm, dd] = match;
  // Next day's midnight in tz; `Date.UTC` handles month/year overflow.
  const nextDayMs = Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd) + 1);
  const next = new Date(nextDayMs);
  const nextDateStr = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
  return wallClockToUtcMs(nextDateStr, "00:00", tz);
}

export function refineCampaignScheduling(
  data: z.infer<typeof campaignSchedulingObject>,
  ctx: z.RefinementCtx,
) {
  if (
    data.campaignEndMode === CampaignEndMode.MaxRecurrences &&
    !data.maxRecurrences
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Enter a number of recurrences",
      path: ["maxRecurrences"],
    });
  }
  if (data.campaignEndMode === CampaignEndMode.SpecificDate) {
    if (!data.scheduleConfig?.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date is required",
        path: ["scheduleConfig"],
      });
    }
  }
  if (data.scheduleConfig?.startDate && data.scheduleConfig?.endDate) {
    // Compare as instants; tz precision isn't needed to detect end < start.
    const startMs = new Date(data.scheduleConfig.startDate).getTime();
    const endMs = new Date(data.scheduleConfig.endDate).getTime();
    if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs <= startMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["scheduleConfig"],
      });
    }
  }
  // Only require a timezone when there's a wall-clock to anchor (manual
  // one-time campaigns leave scheduleConfig null).
  if (
    data.timezoneMode === "fixed" &&
    data.scheduleConfig?.startDate &&
    !data.scheduleConfig.timezone
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Time zone is required",
      path: ["scheduleConfig", "timezone"],
    });
  }
}

/**
 * Pre-`.superRefine` base objects are exported so callers can `.pick()` —
 * `ZodEffects` (the `.superRefine` return type) doesn't support it.
 */
export const campaignSettingsBase = messageConfigurationSchema.extend(
  campaignSchedulingFields,
);

export const smsCampaignSettingsBase = smsMessageConfigurationSchema.extend({
  ...campaignSchedulingFields,
  senderIdentityId: z.string().optional(),
});

export const pushCampaignSettingsBase = pushMessageConfigurationSchema.extend(
  campaignSchedulingFields,
);

/** Campaign-specific scheduling fields — extends messageConfigurationSchema. */
export const campaignSettingsSchema = campaignSettingsBase.superRefine(
  refineCampaignScheduling,
);

/**
 * SMS campaign settings — no subject/preheader. Sender identity is derived from
 * the topic at save time; `senderIdentityId` stays optional for shared form state.
 */
export const smsCampaignSettingsSchema = smsCampaignSettingsBase.superRefine(
  refineCampaignScheduling,
);

export const pushCampaignSettingsSchema = pushCampaignSettingsBase.superRefine(
  refineCampaignScheduling,
);

// Sender identity validation schemas for CEP settings dialogs
export const emailSenderIdentitySchema = z
  .object({
    name: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, "Name is required")),
    fromEmail: z
      .string()
      .transform((v) => v.trim())
      .pipe(
        z
          .string()
          .min(1, "From email is required")
          .email("Invalid email format"),
      ),
    replyToEmail: z
      .string()
      .transform((v) => v.trim())
      .pipe(
        z
          .string()
          .refine(
            (v) => v === "" || parseReplyTo(v) !== null,
            "Enter a valid reply-to (e.g. support@acme.com or Support Team <support@acme.com>)",
          ),
      ),
  })
  .passthrough();

export const smsSenderIdentitySchema = z
  .object({
    displayName: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, "Display name is required")),
    messagingServiceId: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, "Messaging service ID is required")),
  })
  .passthrough();

export const subscriptionTopicSchema = z
  .object({
    name: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, "Name is required")),
    externalIdentifier: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, "External identifier cannot be empty"))
      .optional(),
    policy: z.enum([SubscriptionPolicy.OptIn, SubscriptionPolicy.OptOut], {
      message: "Policy must be opt_in or opt_out",
    }),
  })
  .passthrough();

// Add-subscription-topic dialog schema. A topic links to many sender
// identities (junction table); the dialog binds the set as an array.
const subscriptionTopicFormBaseSchema = subscriptionTopicSchema.extend({
  senderIdentityIds: z.array(z.string()).default([]),
  pushAppId: z.string().nullable().optional(),
});

export function isSenderIdentityRequiredForSubscriptionTopic(
  channelType: ChannelType,
): boolean {
  return channelType === ChannelType.Email || channelType === ChannelType.Sms;
}

export function subscriptionTopicFormSchemaForChannel(
  channelType: ChannelType,
) {
  return subscriptionTopicFormBaseSchema.superRefine((data, ctx) => {
    if (
      isSenderIdentityRequiredForSubscriptionTopic(channelType) &&
      data.senderIdentityIds.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sender identity is required",
        path: ["senderIdentityIds"],
      });
    }
  });
}

export const subscriptionTopicFormSchema = subscriptionTopicFormBaseSchema;

// Quiet hours configuration (shared between frontend and backend)
export type QuietHoursConfig = {
  enabled: boolean;
  startHour: number;
  startMinute?: number;
  endHour: number;
  endMinute?: number;
};

/** Per-channel link tracking config; a null row means "disabled". */
export type LinkTrackingConfig = {
  enabled: boolean;
  use_provider?: boolean;
  use_short_urls?: boolean;
  tracking_domain?: string;
  utm_params?: Record<string, string>;
  exclude_patterns?: string[];
};

// JSONB config types for cep_sender_identities.config column
export type CepEmailSenderConfig = {
  from_email?: string;
  reply_to_email?: string;
  verification_type?: "domain" | "single_sender";
};

export type CepSmsSenderConfig = {
  messaging_service_id?: string;
};

export type CepSenderConfig = CepEmailSenderConfig | CepSmsSenderConfig;

export type CepIpPools = {
  transactional?: string;
  marketing?: string;
};

export function selectIpPool(
  ipPools: CepIpPools | undefined,
  transactional: boolean,
): string | undefined {
  return ipPools?.[transactional ? "transactional" : "marketing"];
}

// JSONB config type for cep_providers.config column
export type CepProviderConfig = {
  managed?: boolean;
  base_url?: string;
  /** SendGrid subuser username (SendGrid only). */
  subuser_name?: string;
  /** Mailgun sending domain (Mailgun only). */
  domain?: string;
  ip_pools?: CepIpPools;
  secrets?: Record<string, true>;
};

// Provider slugs for CEP delivery providers
export enum ProviderSlug {
  SendGrid = "sendgrid",
  Mailgun = "mailgun",
  Twilio = "twilio",
}

export enum MessageOutcomeStatus {
  Scheduled = "scheduled",
  DroppedVolumeCap = "dropped_volume_cap",
  DroppedQuietHours = "dropped_quiet_hours",
  DroppedQuietHoursNoTimezone = "dropped_quiet_hours_no_timezone",
  DroppedUnsubscribed = "dropped_unsubscribed",
  DroppedBounced = "dropped_bounced",
  DroppedSuppressed = "dropped_suppressed",
  DroppedRepeatSend = "dropped_repeat_send",
  DroppedUserFrequencyLimit = "dropped_user_frequency_limit",
  DroppedInvalidHandle = "dropped_invalid_handle",
  DroppedChannelUnsubscribed = "dropped_channel_unsubscribed",
  DroppedUnsupportedLocale = "dropped_unsupported_locale",
  /**
   * Outbox row aged past `expires_at` without ever being admitted to a drain
   * wave. Written by Phase 5 self-sweep, attributed to the sweeping execution.
   * Distinct from `Dropped*` (which are gate decisions) so reporting can show
   * "we ran out of time" separately from "we deliberately filtered."
   */
  TimedOut = "timed_out",
}

export enum DispatcherDropReason {
  RepeatSend = "dispatcher_dropped_repeat_send",
  UserFrequencyLimit = "dispatcher_dropped_user_frequency_limit",
  Unsubscribed = "dispatcher_dropped_unsubscribed",
  Bounced = "dispatcher_dropped_bounced",
  Suppressed = "dispatcher_dropped_suppressed",
  VolumeCap = "dispatcher_dropped_volume_cap",
  QuietHoursNoTimezone = "dispatcher_dropped_quiet_hours_no_timezone",
  QuietHours = "dispatcher_dropped_quiet_hours",
  ChannelUnsubscribed = "dispatcher_dropped_channel_unsubscribed",
  TemplateRenderNullValue = "dispatcher_dropped_template_render_null_value",
  /**
   * The Liquid engine threw while rendering — distinct from
   * `TemplateRenderNullValue`, where the render succeeded and produced a blank.
   */
  TemplateRenderError = "dispatcher_dropped_template_render_error",
  ChannelDailyCap = "dispatcher_dropped_channel_daily_cap",
  UnsupportedLocale = "dispatcher_dropped_unsupported_locale",
}

/**
 * A reason a message was deliberately dropped — either an orchestrator
 * gate outcome (Phase 2 sending gates) or a dispatcher last-mile
 * eligibility/render drop. `Scheduled` and `TimedOut` are excluded:
 * neither counts toward `total_dropped`.
 */
export type DropReason =
  | Exclude<
      MessageOutcomeStatus,
      MessageOutcomeStatus.Scheduled | MessageOutcomeStatus.TimedOut
    >
  | DispatcherDropReason;

/**
 * Per-reason breakdown of `campaign_runs.total_dropped`, stored in the
 * `drop_reason_counts` jsonb column (e.g. `{"dropped_bounced": 126779}`).
 */
export type DropReasonCounts = Partial<Record<DropReason, number>>;
